import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useAccessFolderEnterprise } from "@hooks/employeFolder";
import { useFilterGroup } from "@hooks/useFilterGroup";
import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { SvgSortBy } from "@routes/IconeSvg";
import {
  setIsInactiveMode,
  syncDeletedEmployeeLocally
} from "@store/actions/commonData/_employerActions";
import { setEmployeListView } from "@store/actions/enterpriseEmployeData/_employeListActions";
import { AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";

import AddButton from "@components/buttons/AddButton";
import { Menu, Switch } from "@components/tailwindComponents";
import Button from "@components/tailwindComponents/Button";
import DotLoader from "@components/tailwindComponents/DotLoader";
import EmptyData from "@components/tailwindComponents/EmptyData";
import ModalActiveAccess from "@pages/entreprise/recruitments/components/modals/ModalActiveAccess";

import { Users } from "@services/all.services";

import EmployeTabFilter from "../../components/EmployeTabFilter";
import ModalAddEmploye from "../addEmployee/EmployeFormModal";
import useBulkEditList from "../bulkEdit/hooks/useBulkEditList";
import { ACCESS_BATCH } from "../bulkEdit/utils/bulkEditHelpers";
import {
  AccessRender,
  GroupRender,
  HeaderAccessRender,
  HiringDateRender,
  NameRender,
  PostRender,
  StatutRender
} from "./components";
import EmployeeBatchTools from "./components/EmployeeBatchTools";
import { diacriticSensitiveRegex } from "@helpers/_functions";

const ListEmployer = (props) => {
  const defaultFilterData = useMemo(
    () => ({
      employes: [],
      emplacements: [],
      groupes: [],
      showInactive: false,
      searchTerm: ""
    }),
    []
  );
  const employesList = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.employeList?.list || []
  );
  const filterData = useReduxSelector(
    ({ enterpriseEmployeData }) =>
      enterpriseEmployeData.employeListView?.filter || defaultFilterData
  );
  const filteredEmployeeIds = useReduxSelector(
    ({ enterpriseEmployeData }) =>
      enterpriseEmployeData.employeListView?.filteredIds || []
  );
  const deletedEmployeeIds = useReduxSelector(
    ({ commonData }) => commonData.deletedEmployeeIds || []
  );
  const { isGroupIdsMatchFilter } = useFilterGroup();
  const {
    denyAccessOwnFolderEnterprise,
    canSeeEmployeFolder,
    denyExportEmployeeInfo,
    goToEmployerFolder,
    getButtonVariant,
    canCreateImportEmployees
  } = useAccessFolderEnterprise();
  const { t: translate } = useTranslation("pages.employés.listEmployés");
  const { showSuccess, showError, showWarning } = useNotification();
  const [order, setOrder] = useState("up");
  const [isFiltering, setIsFiltering] = useState(true);
  const [showModalAddEmploye, setShowModalAddEmploye] = useState(false);
  const [showModalActiveAccess, setShowModalActiveAccess] = useState(false);
  const [filteredEmployesList, setFilteredEmployesList] = useState([]);
  const [sortType, setSortType] = useState(null);
  const headRef = useRef();
  const denyBulkEdit = useReduxSelector(({ userConnected }) =>
    ACCESS_BATCH.every(({ _id }) => userConnected.accessList?.includes(_id))
  );
  const { isInfoPersAndEmploiSalaryUnavailable } = useBulkEditList();
  const dispatch = useDispatch();
  const sanitizedEmployesList = useMemo(
    () =>
      employesList.filter(
        (employee) => !deletedEmployeeIds.includes(employee?._id?.toString())
      ),
    [deletedEmployeeIds, employesList]
  );

  const updateEmployeListView = useCallback(
    (nextFilterData, nextFilteredEmployesList) => {
      dispatch(
        setEmployeListView({
          filter: {
            ...defaultFilterData,
            ...nextFilterData
          },
          filteredIds: Array.isArray(nextFilteredEmployesList)
            ? nextFilteredEmployesList
              .map((employe) => employe?._id?.toString())
              .filter(Boolean)
            : []
        })
      );
    },
    [defaultFilterData, dispatch]
  );

  const filterEmployesList = useCallback(
    (_employesList, _filterData = {}) => {
      const {
        employes = [],
        emplacements = [],
        groupes = [],
        showInactive,
        searchTerm = ""
      } = _filterData;
      return _employesList
        .filter((employe) => {
          if (searchTerm && searchTerm.trim() !== "") {
            const searchLower = searchTerm.toLowerCase().trim();
            const regexPattern = diacriticSensitiveRegex(searchLower);
            const searchRegex = new RegExp(regexPattern, 'i');

            const nameMatch = searchRegex.test(employe.personnalInformation?.name);
            const matriculeMatch = searchRegex.test(employe.matricule);

            if (!nameMatch && !matriculeMatch) {
              return false;
            }
          }

          if (employes.length && !employes.includes(employe._id)) {
            return false;
          }
          if (
            emplacements.length &&
            !employe?.emplacement?.some((emplacement) =>
              emplacements.includes(emplacement.id)
            )
          ) {
            return false;
          }
          if (!isGroupIdsMatchFilter(employe, groupes)) {
            return false;
          }
          return showInactive
            ? employe.status !== "Actif"
            : employe.status === "Actif";
        })
        .map((employe) => ({
          ...employe,
          buttonVariantClass: getButtonVariant(employe?._id)
        }));
    },
    [denyAccessOwnFolderEnterprise]
  );

  const onFilterClicked = useCallback(
    (newFilterData) => {
      setIsFiltering(true);
      const filters = { ...filterData, ...newFilterData };
      const nextFilteredEmployesList = filterEmployesList(
        sanitizedEmployesList,
        filters
      );
      setFilteredEmployesList(nextFilteredEmployesList);
      updateEmployeListView(filters, nextFilteredEmployesList);
      setIsFiltering(false);
    },
    [filterData, filterEmployesList, sanitizedEmployesList, updateEmployeListView]
  );

  const handleSearchChange = useCallback((searchTerm) => {
    setIsFiltering(true);
    const filters = { ...filterData, searchTerm };
    const nextFilteredEmployesList = filterEmployesList(
      sanitizedEmployesList,
      filters
    );
    setFilteredEmployesList(nextFilteredEmployesList);
    updateEmployeListView(filters, nextFilteredEmployesList);
  }, [filterData, filterEmployesList, sanitizedEmployesList, updateEmployeListView]);

  const toggleShowInactive = useCallback(() => {
    dispatch(setIsInactiveMode(!filterData.showInactive));
    const filters = {
      ...filterData,
      showInactive: !filterData.showInactive
    };
    const nextFilteredEmployesList = filterEmployesList(
      sanitizedEmployesList,
      filters
    );
    setFilteredEmployesList(nextFilteredEmployesList);
    updateEmployeListView(filters, nextFilteredEmployesList);
  }, [dispatch, filterData, filterEmployesList, sanitizedEmployesList, updateEmployeListView]);

  const toggleModalAddEmploye = useCallback(
    () =>
      setShowModalAddEmploye((_showModalAddEmploye) => !_showModalAddEmploye),
    []
  );

  const resendLoginInviteToNotConnected = () => {
    Users.loginInviteNotConnectedUsers()
      .then((_result) => {
        showSuccess("all_invitation_sent");
      })
      .catch((err) => {
        console.log(err);
        showError("error");
      });
  };

  const toggleModalActiveAccess = useCallback(
    () =>
      setShowModalActiveAccess(
        (_showModalActiveAccess) => !_showModalActiveAccess
      ),
    []
  );

  //table cells render
  const noRowsRenderer = useCallback(
    () =>
      (isFiltering || JSON.stringify(defaultFilterData) === JSON.stringify(filterData)) ? ( // Au départ, la liste contient toujours au moins un employé, correction affichage "Aucun résultat" qui clignote au lancement de la page
        <div className="arh-h-[40dvh]">
          <DotLoader size="small" />
        </div>
      ) : (
        <EmptyData>{translate("Aucun résultat à afficher")}</EmptyData>
      ),
    [isFiltering, translate]
  );

  const _goToEmployerFolder = ({ index }) => {
    const employer = filteredEmployesList[index];
    if (deletedEmployeeIds.includes(employer?._id?.toString())) {
      dispatch(syncDeletedEmployeeLocally(employer?._id));
      return;
    }
    goToEmployerFolder({
      employerId: employer?._id,
      employerSalaryId: employer?.emploiSalaryId
    });
  };

  const LinkButtonRender = useCallback(
    ({ rowData }) => {
      const _goToEmployerFolderBtn = (e) => {
        e.stopPropagation();
        if (deletedEmployeeIds.includes(rowData?._id?.toString())) {
          dispatch(syncDeletedEmployeeLocally(rowData?._id));
          return;
        }
        goToEmployerFolder({
          employerId: rowData?._id,
          employerSalaryId: rowData?.emploiSalaryId
        });
      };
      return (
        <Button
          variant={rowData?.buttonVariantClass}
          onClick={_goToEmployerFolderBtn}
        >
          {translate("Voir le dossier")}
        </Button>
      );
    },
    [deletedEmployeeIds, dispatch, goToEmployerFolder, showWarning, translate]
  );

  const sortEmployesList = useCallback((_employesList, _params) => {
    setTimeout(() => setIsFiltering(false), 100);
    return !_params
      ? _employesList
      : _employesList.sort((employeA, employeB) => {
        let hiringDateA =
          employeA.defaultDateHiring && employeA.seniorityDate
            ? employeA.seniorityDate
            : employeA.hiringDate,
          hiringDateB =
            employeB.defaultDateHiring && employeB.seniorityDate
              ? employeB.seniorityDate
              : employeB.hiringDate;
        if (!hiringDateA) hiringDateA = new Date();
        if (!hiringDateB) hiringDateB = new Date();
        return _params === "up"
          ? new Date(hiringDateA) - new Date(hiringDateB)
          : new Date(hiringDateB) - new Date(hiringDateA);
      });
  }, []);

  useEffect(() => {
    const _filteredEmployesList = sortEmployesList(
      filterEmployesList(sanitizedEmployesList, filterData),
      sortType
    );
    setFilteredEmployesList(_filteredEmployesList);

    const nextFilteredIds = Array.isArray(_filteredEmployesList)
      ? _filteredEmployesList
        .map((employe) => employe?._id?.toString())
        .filter(Boolean)
      : [];

    const hasSameFilteredIds =
      nextFilteredIds.length === filteredEmployeeIds.length &&
      nextFilteredIds.every((id, index) => id === filteredEmployeeIds[index]);

    if (!hasSameFilteredIds) {
      updateEmployeListView(filterData, _filteredEmployesList);
    }
  }, [
    filterData,
    filteredEmployeeIds,
    filterEmployesList,
    sanitizedEmployesList,
    sortType,
    sortEmployesList,
    updateEmployeListView
  ]);

  const onSortClicked = () => {
    if (order === "down") setOrder("up");
    if (order === "up") setOrder("down");
    setSortType(order);
    setFilteredEmployesList((_filteredEmployesList) =>
      sortEmployesList(_filteredEmployesList, order)
    );
  };

  const sortByName = () => {
    const sortedData = [...filteredEmployesList];
    let isSortedAsc = !sortedData.some(
      (employe, i) =>
        i > 0 &&
        sortedData[i - 1].personnalInformation.name.localeCompare(
          employe.personnalInformation.name,
          "fr",
          { sensitivity: "base" }
        ) > 0
    );
    if (isSortedAsc) {
      sortedData.sort((a, b) =>
        b.personnalInformation.name.localeCompare(a.personnalInformation.name, "fr", { sensitivity: "base" })
      );
    } else {
      sortedData.sort((a, b) =>
        a.personnalInformation.name.localeCompare(b.personnalInformation.name, "fr", { sensitivity: "base" })
      );
    }
    setFilteredEmployesList(sortedData);
  };

  const importClick = () => {
    if (!canCreateImportEmployees) {
      showWarning(
        "Importation indisponible, veuillez demander l'accès à votre administrateur"
      );
      return;
    }
    props.importList();
  };

  const addEmployeClick = () => {
    if (!canCreateImportEmployees) {
      showWarning(
        "Ajout indisponible, veuillez demander l'accès à votre administrateur"
      );
      return;
    }
    toggleModalAddEmploye();
  };

  const onExportClick = () => {
    if (denyExportEmployeeInfo) {
      showWarning(
        "Exportation indisponible, veuillez demander l'accès à votre administrateur"
      );
      return;
    }
    props.exportList();
  };
  const onBulkEditClick = () => {
    if (isInfoPersAndEmploiSalaryUnavailable) {
      showWarning(
        "Accès limité Veuillez demander l'accès à votre administrateur"
      );
      return;
    }
    if (denyBulkEdit) {
      showWarning(
        "Modification multiple indisponible, veuillez demander l'accès à votre administrateur"
      );
      return;
    }
    props.bulkEditList();
  };

  return (
    <>
      <div className="arh-rounded arh-bg-white arh-p-4 arh-px-9">
        <EmployeTabFilter
          defaultValues={filterData}
          onFilter={onFilterClicked}
          showInactive={filterData.showInactive}
          onSearchChange={handleSearchChange}
          withSearch
        />
        <div className="arh-flex arh-items-center  arh-justify-between">
          <AddButton
            label={translate("Ajouter un employé")}
            onClick={addEmployeClick}
            labelClass={!canCreateImportEmployees && "arh-text-greyDisabledText"}
            color={!canCreateImportEmployees ?
              "arh-bg-greyDisabled" :
              "arh-bg-greenColor hover:arh-bg-darkGreenColor"
            }
          />
          <div className="arh-flex arh-flex-1 arh-items-center arh-justify-end">
            <div className="arh-flex arh-items-center arh-space-x-2 arh-font-poppins arh-text-xs arh-font-medium arh-text-[#7a858a]">
              <Switch
                color="arh-bg-greenColor"
                handleChange={toggleShowInactive}
                checked={filterData.showInactive}
              />
              <span>{translate("Afficher employés inactifs")}</span>
            </div>
            <div className="arh-my-7 arh-ml-5 arh-flex arh-items-center arh-space-x-4">
              <EmployeeBatchTools
                dataSelected={null}
                Placeholder={translate(
                  "Imports, exports et modifications multiples"
                )}
                importList={importClick}
                onExportClick={onExportClick}
                bulkEditList={onBulkEditClick}
                canCreateImportEmployees={canCreateImportEmployees}
                denyBulkEdit={denyBulkEdit}
                denyExportEmployeeInfo={denyExportEmployeeInfo}
                isInfoPersAndEmploiSalaryUnavailable={
                  isInfoPersAndEmploiSalaryUnavailable
                }
              />
              <Menu
                lists={[
                  {
                    title: translate(
                      "Activer l’accès à altee de tous les employés"
                    ),
                    click: () => toggleModalActiveAccess()
                  },
                  {
                    title: translate(
                      "Renvoyer l'invitation à tous les employés sans accès activé"
                    ),
                    click: () => resendLoginInviteToNotConnected()
                  }
                ]}
              />
            </div>
          </div>
        </div>

        <div
          className="arh-relative"
          ref={headRef}
        >
          {!filterData.showInactive ? (
            <AutoSizer disableHeight={true}>
              {({ width }) => (
                <Table
                  noRowsRenderer={noRowsRenderer}
                  width={width}
                  height={830}
                  headerHeight={54}
                  rowHeight={62}
                  onRowClick={_goToEmployerFolder}
                  rowCount={isFiltering ? 0 : filteredEmployesList?.length}
                  headerClassName="arh-font-semibold arh-text-xs arh-text-black arh-px-4 arh-py-4"
                  className="customTable arh-relative arh-table-fixed arh-font-poppins"
                  rowClassName="arh-cursor-pointer"
                  rowGetter={({ index }) => filteredEmployesList[index]}
                >
                  <Column
                    label={translate("Nom")}
                    dataKey="name"
                    cellDataGetter={({ rowData }) =>
                      rowData.personnalInformation.name
                    }
                    width={width * 0.2}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    headerRenderer={() => (
                      <div className="blackSvgColor arh-flex arh-items-center arh-justify-start">
                        <span className="arh-text-xs arh-text-darkColor">
                          {translate("Nom")}
                        </span>
                        <span
                          className="arh-ml-2 arh-cursor-pointer"
                          onClick={sortByName}
                        >
                          {SvgSortBy()}
                        </span>
                      </div>
                    )}
                    cellRenderer={({ rowData }) => (
                      <NameRender
                        rowData={rowData}
                        headRef={headRef}
                      />
                    )}
                  />
                  <Column
                    label={translate("Poste")}
                    dataKey="poste"
                    width={width * 0.2}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData, rowIndex }) => (
                      <PostRender
                        rowData={rowData}
                        rowIndex={rowIndex}
                        t={translate}
                      />
                    )}
                  />
                  <Column
                    label={translate("Groupes")}
                    dataKey="groupesName"
                    width={width * 0.2}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData, rowIndex }) => (
                      <GroupRender
                        rowData={rowData}
                        rowIndex={rowIndex}
                        t={translate}
                      />
                    )}
                  />
                  <Column
                    label={translate("Date d'embauche")}
                    headerRenderer={() => (
                      <div className="blackSvgColor arh-flex arh-items-center arh-justify-between">
                        <span className="arh-text-xs  arh-font-semibold arh-text-darkColor">
                          {translate("Date d'embauche")}
                        </span>
                        <span
                          className="arh-ml-2 arh-cursor-pointer"
                          onClick={onSortClicked}
                        >
                          {SvgSortBy()}
                        </span>
                      </div>
                    )}
                    dataKey="embauche"
                    width={width * (canSeeEmployeFolder ? 0.15 : 0.2)}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData }) => (
                      <HiringDateRender rowData={rowData} />
                    )}
                  />
                  <Column
                    label={translate("Accès à l'Altee")}
                    dataKey="accès"
                    width={width * (canSeeEmployeFolder ? 0.15 : 0.2)}
                    className="!arh-overflow-visible arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    headerRenderer={(headerData) => (
                      <HeaderAccessRender headerData={headerData} />
                    )}
                    cellRenderer={({ rowData }) => (
                      <AccessRender rowData={rowData} />
                    )}
                  />
                  {canSeeEmployeFolder && (
                    <Column
                      label=""
                      dataKey="dossier"
                      width={width * 0.15}
                      className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                      cellRenderer={LinkButtonRender}
                    />
                  )}
                </Table>
              )}
            </AutoSizer>
          ) : (
            <AutoSizer disableHeight={true}>
              {({ width }) => (
                <Table
                  noRowsRenderer={noRowsRenderer}
                  width={width}
                  height={700}
                  headerHeight={54}
                  rowHeight={62}
                  rowCount={isFiltering ? 0 : filteredEmployesList?.length}
                  headerClassName="arh-font-semibold arh-text-xs arh-text-black arh-px-4 arh-py-4"
                  className="customTable arh-relative arh-table-fixed arh-font-poppins"
                  rowGetter={({ index }) => filteredEmployesList[index]}
                  rowClassName="arh-cursor-pointer"
                >
                  <Column
                    label={translate("Nom")}
                    dataKey="name"
                    cellDataGetter={({ rowData }) =>
                      rowData.personnalInformation.name
                    }
                    width={width * 0.2}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    headerRenderer={() => (
                      <div className="blackSvgColor arh-flex arh-items-center arh-justify-start">
                        <span className="arh-text-xs arh-text-darkColor">
                          {translate("Nom")}
                        </span>
                        <span
                          className="arh-ml-2 arh-cursor-pointer"
                          onClick={sortByName}
                        >
                          {SvgSortBy()}
                        </span>
                      </div>
                    )}
                    cellRenderer={({ rowData }) => (
                      <NameRender
                        rowData={rowData}
                        headRef={headRef}
                      />
                    )}
                  />
                  <Column
                    label={translate("Poste")}
                    dataKey="poste"
                    width={width * 0.2}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData, rowIndex }) => (
                      <PostRender
                        rowData={rowData}
                        rowIndex={rowIndex}
                        t={translate}
                      />
                    )}
                  />
                  <Column
                    label={translate("Statut")}
                    dataKey="statut"
                    width={width * (canSeeEmployeFolder ? 0.15 : 0.2)}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData }) => (
                      <StatutRender rowData={rowData} />
                    )}
                  />
                  <Column
                    label={translate("Groupes")}
                    dataKey="groupeName"
                    width={width * 0.15}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData, rowIndex }) => (
                      <GroupRender
                        rowData={rowData}
                        rowIndex={rowIndex}
                        t={translate}
                      />
                    )}
                  />
                  <Column
                    label={translate("Date d'embauche")}
                    headerRenderer={() => (
                      <div className="blackSvgColor arh-flex arh-items-center arh-justify-between">
                        <span className="arh-text-xs arh-text-darkColor">
                          {translate("Date d'embauche")}
                        </span>
                        <span
                          className="arh-ml-2 arh-cursor-pointer"
                          onClick={onSortClicked}
                        >
                          {SvgSortBy()}
                        </span>
                      </div>
                    )}
                    dataKey="embauche"
                    width={width * (canSeeEmployeFolder ? 0.15 : 0.2)}
                    className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                    cellRenderer={({ rowData }) => (
                      <HiringDateRender rowData={rowData} />
                    )}
                  />
                  {canSeeEmployeFolder && (
                    <Column
                      label=""
                      dataKey="dossier"
                      width={width * 0.15}
                      className="arh-px-4 arh-py-2 arh-text-xs arh-font-medium arh-text-darkColor"
                      cellRenderer={LinkButtonRender}
                    />
                  )}
                </Table>
              )}
            </AutoSizer>
          )}
        </div>
      </div>
      <ModalAddEmploye
        show={showModalAddEmploye}
        fragment={Fragment}
        onClickClose={toggleModalAddEmploye}
      />
      <ModalActiveAccess
        show={showModalActiveAccess}
        onCloseClicked={toggleModalActiveAccess}
      />
    </>
  );
};

export default memo(ListEmployer);
